/**
 * PostHog: Nur bei Einwilligung geladen (100 % DSGVO/TTDSG).
 * - Script wird erst per dynamic import geladen, wenn Nutzer "Akzeptieren" wählt oder gespeicherte Wahl "accepted" ist.
 * - Ohne Consent: kein PostHog-Skript, keine Cookies, keine Requests an PostHog.
 * - Mit Consent: EU-Host (Frankfurt), opt_in, volles Tracking.
 */

import { getStoredAnalyticsConsent, type AnalyticsConsentStatus } from "./analytics-consent.js";
import { toDisplayString } from "./utils.js";
/** See config.ts: `import.meta.env` is bundler-only and throws under plain Node. This module is
 *  pulled in by api.ts (trackEvent), so it has to survive being imported outside Vite. */
import { viteEnv } from "../config.js";

const EU_HOST = "https://eu.i.posthog.com";

/**
 * PostHog patchen DOM/React; auf Login/OAuth-Callback hat das wiederholt
 * "Cannot convert object to primitive value" ausgelöst. Erst nach Verlassen
 * dieser Routen initialisieren (siehe PostHogPageView + initPostHog guard).
 */
const DEFER_POSTHOG_PATH_PREFIXES = ["/login", "/auth/callback"] as const;

export function shouldDeferPostHogForPath(pathname: string): boolean {
  const p = pathname.split("?")[0] ?? pathname;
  return DEFER_POSTHOG_PATH_PREFIXES.some(
    (prefix) => p === prefix || p.startsWith(`${prefix}/`)
  );
}

type PostHogInstance = {
  init: (key: string, opts?: Record<string, unknown>) => void;
  opt_in_capturing: () => void;
  opt_out_capturing: () => void;
  has_opted_out_capturing: () => boolean;
  capture: (event: string, props?: Record<string, unknown>) => void;
  identify: (id: string, traits?: Record<string, unknown>) => void;
};

let posthogInstance: PostHogInstance | null = null;
let posthogInitialized = false;
let posthogInitInFlight = false;

/** Nur JSON-taugliche / stringifizierbare Werte — vermeidet SDK-interne ToPrimitive-/Serialize-Crashes. */
function sanitizePostHogValue(v: unknown): unknown {
  if (v == null) return v;
  const t = typeof v;
  if (t === "string" || t === "number" || t === "boolean") return v;
  if (t === "bigint") return toDisplayString(v);
  if (t === "symbol") return toDisplayString(v);
  if (t === "function") return "[function]";
  if (Array.isArray(v)) return v.map(sanitizePostHogValue);
  if (v instanceof Date) return v.toISOString();
  try {
    return JSON.parse(JSON.stringify(v)) as unknown;
  } catch {
    return toDisplayString(v);
  }
}

function sanitizePostHogProperties(
  properties: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (properties == null || typeof properties !== "object") return {};
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(properties)) {
    out[toDisplayString(k, "key")] = sanitizePostHogValue(v);
  }
  return out;
}

/** Letzter Pageview bis SDK fertig ist (Navigation kann vor init()-Resolve passieren). */
let pendingPageView: { path: string; routeId?: string } | null = null;

function flushPendingPageView(): void {
  if (!pendingPageView || !posthogInstance || !posthogInitialized) return;
  if (posthogInstance.has_opted_out_capturing()) {
    pendingPageView = null;
    return;
  }
  const { path, routeId } = pendingPageView;
  pendingPageView = null;
  const url = typeof window !== "undefined" ? window.location.href : path;
  try {
    posthogInstance.capture("$pageview", {
      $current_url: url,
      path,
      ...(routeId ? { route_id: routeId } : {}),
    });
  } catch {
    /* ignore */
  }
}

/**
 * Auf /login und /auth/callback darf PostHog nicht aktiv erfassen (SDK kann mit React 19 kollidieren).
 * Wenn das SDK bereits von einer anderen Route geladen wurde, temporär opt-out; nach Verlassen wieder opt-in bei Einwilligung.
 */
export function syncPostHogWithRoute(pathname: string): void {
  if (!posthogInstance || !posthogInitialized) return;
  try {
    if (shouldDeferPostHogForPath(pathname)) {
      posthogInstance.opt_out_capturing();
      return;
    }
    if (getStoredAnalyticsConsent() === "accepted") {
      posthogInstance.opt_in_capturing();
    }
  } catch {
    /* ignore */
  }
}

function getPostHogKey(): string | null {
  const disabled = viteEnv().VITE_POSTHOG_DISABLED as string | undefined;
  if (disabled === "1" || disabled === "true") return null;
  const key = viteEnv().VITE_POSTHOG_KEY as string | undefined;
  return key?.trim() || null;
}

/** Gibt an, ob PostHog konfiguriert ist (Key gesetzt). */
export function isPostHogAvailable(): boolean {
  return Boolean(getPostHogKey());
}

/**
 * Lädt PostHog nur bei Einwilligung (dynamic import). Darf ausschließlich aufgerufen werden, wenn
 * getStoredAnalyticsConsent() === "accepted" (gespeichert oder soeben gewählt).
 * Kein Script-Download, keine Cookies, keine Erfassung ohne vorherigen Accept.
 * Zentraler Consent-Check: verhindert versehentliches Laden ohne gespeicherte "accepted".
 *
 * Initialisierung einen Animation-Frame verzögert + kein automatisches `capture_pageview`:
 * Reduziert Kollisionen mit React-19-Reconciliation (ToPrimitive-Crashes nach Login).
 */
export function initPostHog(): void {
  const key = getPostHogKey();
  if (!key) return;
  if (getStoredAnalyticsConsent() !== "accepted") return;
  if (posthogInitialized && posthogInstance) return;
  if (typeof window !== "undefined" && shouldDeferPostHogForPath(window.location.pathname)) {
    return;
  }
  if (posthogInitInFlight) return;
  posthogInitInFlight = true;

  const loadAndInit = (): void => {
    import("posthog-js")
      .then((m) => {
        if (posthogInitialized && posthogInstance) return;
        const posthog = m.default as PostHogInstance;
        try {
          posthog.init(key, {
            api_host: EU_HOST,
            ui_host: "https://eu.posthog.com",
            persistence: "localStorage+cookie",
            person_profiles: "identified_only",
            /** History/DOM-Hooks des SDK können mit React 19 kollidieren — Pageviews nur per capturePageView(). */
            capture_pageview: false,
            /** Default true: Pageleave-Listener können in SPAs mit React 19 kollidieren. */
            capture_pageleave: false,
            /** Default true: Dead-Click-Capture patcht DOM. */
            capture_dead_clicks: false,
            /** Default true: Rageclick-Heuristik berührt DOM. */
            rageclick: false,
            /** Kein Survey-/Preact-Bundle, kein /flags-abhängiges Verhalten — nur capture + identify. */
            disable_surveys: true,
            /**
             * Kein /flags-Request: schaltet Toolbar, Remote-Surveys, Feature-Flag-Side-Effects aus.
             * Laut PostHog-Doku bleiben posthog.capture, identify, $set erhalten.
             * Trade-off: keine Feature Flags / Toolbar im Browser — bei Bedarf wieder false setzen.
             */
            advanced_disable_flags: true,
            /** Weniger DOM-Injektion; empfohlen für React-Router/SPA. */
            external_scripts_inject_target: "head",
            /** Avoid extra bundles from eu-assets (web-vitals, exception/dead-click autocapture) — CSP + fewer React/DOM hooks pre-login. */
            capture_performance: false,
            capture_exceptions: false,
            /** DOM-Autocapture kann mit React 19 kollidieren; Pageviews/Events bleiben per capture(). */
            autocapture: false,
            /** Session replay pulls large scripts; re-enable when CSP + stability confirmed. */
            disable_session_recording: true,
            enable_heatmaps: false,
          });
          posthog.opt_in_capturing();
          posthogInstance = posthog;
          posthogInitialized = true;
          if (typeof window !== "undefined") {
            syncPostHogWithRoute(window.location.pathname);
          }
          flushPendingPageView();
        } catch {
          posthogInstance = null;
        }
      })
      .catch(() => {})
      .finally(() => {
        posthogInitInFlight = false;
      });
  };

  if (typeof window !== "undefined") {
    /**
     * Nach Doppel-rAF: `requestIdleCallback` (max. ~2,5 s) oder Fallback-Timeout.
     * PostHog patcht DOM/Listener — zu nah am Router-Commit löste bei React 19 wiederholt
     * ToPrimitive-Crashes aus; Idle + Micro-Delay trennt SDK-Start von der Reconciliation.
     */
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const ric = window.requestIdleCallback?.bind(window);
        if (typeof ric === "function") {
          ric(() => window.setTimeout(loadAndInit, 0), { timeout: 2500 });
        } else {
          window.setTimeout(loadAndInit, 450);
        }
      });
    });
  } else {
    loadAndInit();
  }
}

/**
 * Wendet Widerruf an (opt_out). Nur relevant, wenn PostHog zuvor nach Accept geladen wurde.
 */
export function applyPostHogConsent(status: AnalyticsConsentStatus): void {
  if (status === "accepted") {
    initPostHog();
    return;
  }
  if (posthogInstance && posthogInitialized) {
    posthogInstance.opt_out_capturing();
  }
}

/*
 * `getPostHog()` entfernt (Phase 7 Plan 09): null Aufrufer. Es reichte die Instanz roh
 * nach außen und hätte damit jede Consent-Prüfung dieses Moduls umgehbar gemacht —
 * `capturePostHogEvent` und `identifyUser` prüfen `has_opted_out_capturing()`, ein
 * direkter `posthog.capture()` über den Getter nicht.
 */

export function identifyUser(distinctId: string, traits?: Record<string, unknown>): void {
  if (!posthogInstance || !posthogInitialized) return;
  if (posthogInstance.has_opted_out_capturing()) return;
  try {
    const id = toDisplayString(distinctId, "").trim();
    if (!id) return;
    posthogInstance.identify(id, sanitizePostHogProperties(traits));
  } catch {
    /* identify darf nie die App killen */
  }
}

export function capturePostHogEvent(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  if (!getPostHogKey() || !posthogInstance || !posthogInitialized) return;
  if (posthogInstance.has_opted_out_capturing()) return;
  try {
    const name = toDisplayString(eventName, "").trim();
    if (!name) return;
    posthogInstance.capture(name, sanitizePostHogProperties(properties));
  } catch {
    /* capture darf nie die App killen */
  }
}

export function capturePageView(path: string, routeId?: string): void {
  if (!getPostHogKey()) return;
  pendingPageView = { path, routeId };
  if (!posthogInstance || !posthogInitialized) return;
  if (posthogInstance.has_opted_out_capturing()) {
    pendingPageView = null;
    return;
  }
  flushPendingPageView();
}
